/**
 * Super-admin "Workspace controls": the gates that can stop a workspace doing
 * work, in one place, so unblocking a pilot is not a SQL session. Plans gate
 * nothing; every workspace has the full feature set.
 *
 * Money is entered and displayed in DOLLARS; the wire format is millicents
 * (millicents / 100_000 = dollars). The conversion lives only at this boundary.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Gauge, Users, AlertTriangle, Infinity as InfinityIcon } from "lucide-react";
import type {
  AdminTeamLinePatch,
  AdminWorkspaceControlsResponse,
  AdminWorkspaceScopingControl,
  AdminWorkspaceTeamJobLine,
  AdminWorkspaceTeamsResponse,
} from "@yagni/shared";
import {
  DEFAULT_WORKSPACE_MAX_CONCURRENT_RUNS,
  WORKSPACE_MAX_CONCURRENT_RUNS_LIMIT,
  jobReadinessPhrase,
} from "@yagni/shared";
import { formatRelative } from "../../lib/relativeTime";
import {
  TeamLaneSelect,
  TeamLineControls,
  WorkspaceLaneSelect,
  workspaceOverride,
} from "./TeamLineControls";

const MILLICENTS_PER_DOLLAR = 100_000;

function millicentsToDollars(millicents: number): number {
  return millicents / MILLICENTS_PER_DOLLAR;
}

/**
 * Dollars → whole millicents. Rounds rather than truncates so a value typed to
 * the cent lands exactly, and the backend rejects non-integers outright.
 */
function dollarsToMillicents(dollars: number): number {
  return Math.round(dollars * MILLICENTS_PER_DOLLAR);
}

function formatDollars(millicents: number): string {
  return millicentsToDollars(millicents).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

interface Props {
  workspaceId: string;
}

/** The scoping cap in force, in words. */
function scopingStatus(scoping: AdminWorkspaceScopingControl): string {
  if (scoping.source === "stored") return `${scoping.sessionsPerDay} a day`;
  if (scoping.source === "unreadable")
    return "Could not read the stored cap; the default is in force";
  return `Using the default: ${scoping.defaultSessionsPerDay} a day`;
}

/** How long a line save may hang before the editor says so and frees Save. */
const LINE_SAVE_TIMEOUT_MS = 30_000;

export function WorkspaceControlsPanel({ workspaceId }: Props) {
  const [data, setData] = useState<AdminWorkspaceControlsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [ceilingInput, setCeilingInput] = useState("");
  const [runsInput, setRunsInput] = useState("");
  const [scopingInput, setScopingInput] = useState("");
  const [capMemberId, setCapMemberId] = useState("");
  const [capInput, setCapInput] = useState("");
  const [suspendReason, setSuspendReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/workspace-controls/${encodeURIComponent(workspaceId)}`);
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? `Request failed (${res.status})`);
      }
      const body = (await res.json()) as AdminWorkspaceControlsResponse;
      setData(body);
      setCeilingInput(String(millicentsToDollars(body.capacity.dailyCeilingMillicents)));
      setRunsInput(body.maxConcurrentRuns == null ? "" : String(body.maxConcurrentRuns));
      setScopingInput(body.scoping ? String(body.scoping.sessionsPerDay) : "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load controls");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Every mutation returns the refreshed payload, so one helper covers all. */
  const mutate = useCallback(
    async (key: string, path: string, init: RequestInit) => {
      setBusy(key);
      setError(null);
      try {
        const res = await fetch(
          `/api/admin/workspace-controls/${encodeURIComponent(workspaceId)}${path}`,
          { headers: { "content-type": "application/json" }, ...init },
        );
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? `Request failed (${res.status})`);
        }
        const body = (await res.json()) as AdminWorkspaceControlsResponse;
        setData(body);
        setCeilingInput(String(millicentsToDollars(body.capacity.dailyCeilingMillicents)));
        setRunsInput(body.maxConcurrentRuns == null ? "" : String(body.maxConcurrentRuns));
        setScopingInput(body.scoping ? String(body.scoping.sessionsPerDay) : "");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Request failed");
      } finally {
        setBusy(null);
      }
    },
    [workspaceId],
  );

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 text-[13px]"
        style={{ color: "var(--text-secondary)" }}
      >
        <Loader2 size={14} className="animate-spin" />
        Loading workspace controls…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-[13px]" style={{ color: "var(--error)" }}>
        {error ?? "Failed to load workspace controls."}
      </div>
    );
  }

  const ceilingDollars = Number(ceilingInput);
  // `Number("") === 0`, so an empty field must be rejected explicitly: a
  // transient clear would otherwise read as the documented unlimited value and
  // the Save button would write zero, silently removing the spend ceiling.
  const ceilingMillis = dollarsToMillicents(ceilingDollars);
  const ceilingValid =
    ceilingInput.trim() !== "" &&
    Number.isFinite(ceilingDollars) &&
    ceilingDollars >= 0 &&
    Number.isSafeInteger(ceilingMillis);
  const ceilingUnlimited = data.capacity.dailyCeilingMillicents === 0;

  // Empty restores the platform default; anything else is a whole number in
  // range. Unlike the ceiling, empty is a real value here (null), not a zero.
  const runsTrimmed = runsInput.trim();
  const runsValue = runsTrimmed === "" ? null : Number(runsTrimmed);
  const runsValid =
    runsValue === null ||
    (Number.isInteger(runsValue) &&
      runsValue >= 1 &&
      runsValue <= WORKSPACE_MAX_CONCURRENT_RUNS_LIMIT);
  const runsInForce =
    data.effectiveMaxConcurrentRuns ??
    data.maxConcurrentRuns ??
    DEFAULT_WORKSPACE_MAX_CONCURRENT_RUNS;

  const scoping = data.scoping ?? null;
  const scopingValue = Number(scopingInput.trim());
  const scopingValid =
    scoping !== null &&
    scopingInput.trim() !== "" &&
    Number.isInteger(scopingValue) &&
    scopingValue >= 1 &&
    scopingValue <= scoping.maxSessionsPerDay;

  const capDollars = Number(capInput);
  const capMillis = dollarsToMillicents(capDollars);
  const capValid =
    capInput.trim() !== "" &&
    Number.isFinite(capDollars) &&
    capDollars >= 0 &&
    Number.isSafeInteger(capMillis) &&
    capMemberId !== "";

  return (
    <div className="space-y-8" data-testid="workspace-controls">
      {error && (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded text-[12px]"
          style={{ background: "var(--error-muted, rgba(200,40,40,0.08))", color: "var(--error)" }}
          role="alert"
        >
          <AlertTriangle size={13} />
          {error}
        </div>
      )}

      {/* ── Deactivate (suspension) ──────────────────────────────── */}
      <section data-testid="workspace-suspension">
        <h2 className="text-[14px] font-semibold mb-1" style={{ color: "var(--text-primary)" }}>
          Deactivate
        </h2>
        {data.suspended ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-[12px]" style={{ color: "var(--error)" }}>
              This workspace is deactivated. Sign-ins, runs, tokens, connectors, webhooks and model
              calls all refuse, and it is off Systems, Stream and the Workspaces list, until you
              reactivate it.
            </p>
            <button
              type="button"
              disabled={busy === "suspension"}
              onClick={() =>
                void mutate("suspension", "/suspension", {
                  method: "PATCH",
                  body: JSON.stringify({ suspended: false }),
                })
              }
              className="px-2 py-1 rounded text-[11px] border-none cursor-pointer disabled:opacity-50 shrink-0"
              style={{ background: "var(--accent-muted)", color: "var(--accent)" }}
            >
              Reactivate
            </button>
          </div>
        ) : (
          <>
            <p className="text-[12px] mb-2" style={{ color: "var(--text-secondary)" }}>
              Suspends the workspace: everything it does stops, live runs are asked to stop, and
              incoming webhooks are dropped. It leaves Systems, Stream and the Workspaces list, and
              Reactivate brings it all back. The reason is kept on the audit record.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                aria-label="Reason for deactivating"
                placeholder="Reason, e.g. inactive test account"
                value={suspendReason}
                maxLength={500}
                onChange={(e) => setSuspendReason(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1 rounded border text-[12px]"
                style={{
                  borderColor: "var(--border)",
                  background: "var(--bg-input)",
                  color: "var(--text-primary)",
                }}
              />
              <button
                type="button"
                disabled={busy === "suspension" || suspendReason.trim() === ""}
                onClick={() =>
                  void mutate("suspension", "/suspension", {
                    method: "PATCH",
                    body: JSON.stringify({ suspended: true, reason: suspendReason.trim() }),
                  }).then(() => setSuspendReason(""))
                }
                className="px-2 py-1 rounded text-[11px] border-none cursor-pointer disabled:opacity-50 shrink-0"
                style={{ background: "var(--error-muted)", color: "var(--error)" }}
              >
                Deactivate workspace
              </button>
            </div>
          </>
        )}
      </section>

      {/* ── Managed by YAGNI ─────────────────────────────────────── */}
      <section data-testid="workspace-managed-by-yagni">
        <h2 className="text-[14px] font-semibold mb-1" style={{ color: "var(--text-primary)" }}>
          Managed by YAGNI
        </h2>
        <div className="flex items-center justify-between gap-3">
          <p className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
            {data.managedByYagni
              ? "On. A Team created here starts Bailey, Reeve, Wright and Proctor autonomous. Teams that already exist keep their rungs."
              : "Off. A Team created here starts supervised. Turn it on for a workspace YAGNI runs, and new Teams start autonomous."}
          </p>
          <button
            type="button"
            role="switch"
            aria-checked={data.managedByYagni === true}
            aria-label="Managed by YAGNI"
            disabled={busy === "managed"}
            onClick={() =>
              void mutate("managed", "/managed-by-yagni", {
                method: "PATCH",
                body: JSON.stringify({ enabled: data.managedByYagni !== true }),
              })
            }
            className="px-2 py-1 rounded text-[11px] border-none cursor-pointer disabled:opacity-50 shrink-0"
            style={{ background: "var(--accent-muted)", color: "var(--accent)" }}
          >
            {busy === "managed" ? (
              <Loader2 size={11} className="animate-spin" />
            ) : data.managedByYagni ? (
              "Turn off"
            ) : (
              "Turn on"
            )}
          </button>
        </div>
      </section>

      {/* ── Capacity ─────────────────────────────────────────────── */}
      <section>
        <h2
          className="text-[14px] font-semibold mb-1 flex items-center gap-2"
          style={{ color: "var(--text-primary)" }}
        >
          <Gauge size={14} style={{ color: "var(--accent)" }} />
          Capacity
        </h2>
        <p className="text-[12px] mb-3" style={{ color: "var(--text-secondary)" }}>
          Workspace-wide daily cost ceiling. <strong>0 means unlimited</strong>: the check only
          blocks when the ceiling is above zero.
        </p>
        <div className="flex items-end gap-3 flex-wrap">
          <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
            <span className="block mb-1">Daily ceiling ($/day)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={ceilingInput}
              onChange={(e) => setCeilingInput(e.target.value)}
              aria-label="Daily ceiling in dollars"
              className="px-2 py-1 rounded border text-[13px] w-40"
              style={{ borderColor: "var(--border)", color: "var(--text-primary)" }}
            />
          </label>
          <button
            type="button"
            disabled={busy === "capacity" || !ceilingValid}
            onClick={() =>
              void mutate("capacity", "/capacity", {
                method: "PATCH",
                body: JSON.stringify({
                  dailyCeilingMillicents: ceilingMillis,
                }),
              })
            }
            className="px-3 py-1.5 rounded text-[12px] border-none cursor-pointer disabled:opacity-50"
            style={{ background: "var(--accent-muted)", color: "var(--accent)" }}
          >
            {busy === "capacity" ? <Loader2 size={11} className="animate-spin" /> : "Save ceiling"}
          </button>
          <div
            className="text-[12px]"
            style={{ color: "var(--text-secondary)" }}
            data-testid="ceiling-status"
          >
            {ceilingUnlimited ? (
              <span className="inline-flex items-center gap-1">
                <InfinityIcon size={12} /> Unlimited
              </span>
            ) : (
              <>Used today: {formatDollars(data.capacity.dailyUsedMillicents)}</>
            )}
            {" · "}
            Credit balance: {data.capacity.creditBalance.toLocaleString()}
          </div>
        </div>
        {!ceilingValid && (
          <p className="text-[11px] mt-1" style={{ color: "var(--error)" }}>
            Enter a non-negative dollar amount (0 removes the ceiling).
          </p>
        )}
        <div className="flex items-end gap-3 flex-wrap mt-4" data-testid="concurrency-control">
          <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
            <span className="block mb-1">Runs at once</span>
            <input
              type="number"
              min="1"
              max={WORKSPACE_MAX_CONCURRENT_RUNS_LIMIT}
              step="1"
              value={runsInput}
              placeholder={String(DEFAULT_WORKSPACE_MAX_CONCURRENT_RUNS)}
              onChange={(e) => setRunsInput(e.target.value)}
              aria-label="Runs at once"
              aria-describedby="concurrency-help"
              className="px-2 py-1 rounded border text-[13px] w-40"
              style={{ borderColor: "var(--border)", color: "var(--text-primary)" }}
            />
          </label>
          <button
            type="button"
            disabled={busy === "concurrency" || !runsValid}
            onClick={() =>
              void mutate("concurrency", "/concurrency", {
                method: "PATCH",
                body: JSON.stringify({ maxConcurrentRuns: runsValue }),
              })
            }
            className="px-3 py-1.5 rounded text-[12px] border-none cursor-pointer disabled:opacity-50"
            style={{ background: "var(--accent-muted)", color: "var(--accent)" }}
          >
            {busy === "concurrency" ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              "Save runs at once"
            )}
          </button>
          <div
            className="text-[12px]"
            style={{ color: "var(--text-secondary)" }}
            data-testid="concurrency-status"
          >
            {data.maxConcurrentRuns == null
              ? `Using the default: ${runsInForce} at once`
              : `${runsInForce} at once`}
          </div>
        </div>
        <p
          id="concurrency-help"
          className="text-[11px] mt-1"
          style={{ color: "var(--text-secondary)" }}
        >
          How many agent runs this organization can have going at once. Extra work waits its turn
          and starts on its own. Empty uses the default ({DEFAULT_WORKSPACE_MAX_CONCURRENT_RUNS}).
        </p>
        {!runsValid && (
          <p className="text-[11px] mt-1" style={{ color: "var(--error)" }}>
            Enter a whole number from 1 to {WORKSPACE_MAX_CONCURRENT_RUNS_LIMIT}, or leave it empty
            for the default.
          </p>
        )}
        {scoping && (
          <>
            <div className="flex items-end gap-3 flex-wrap mt-4" data-testid="scoping-control">
              <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
                <span className="block mb-1">Scoping sessions a day</span>
                <input
                  type="number"
                  min="1"
                  max={scoping.maxSessionsPerDay}
                  step="1"
                  value={scopingInput}
                  onChange={(e) => setScopingInput(e.target.value)}
                  aria-label="Scoping sessions a day"
                  aria-describedby="scoping-help"
                  className="px-2 py-1 rounded border text-[13px] w-40"
                  style={{ borderColor: "var(--border)", color: "var(--text-primary)" }}
                />
              </label>
              <button
                type="button"
                disabled={busy === "scoping" || !scopingValid}
                onClick={() =>
                  void mutate("scoping", "/scoping", {
                    method: "PATCH",
                    body: JSON.stringify({ sessionsPerDay: scopingValue }),
                  })
                }
                className="px-3 py-1.5 rounded text-[12px] border-none cursor-pointer disabled:opacity-50"
                style={{ background: "var(--accent-muted)", color: "var(--accent)" }}
              >
                {busy === "scoping" ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  "Save scoping cap"
                )}
              </button>
              <div
                className="text-[12px]"
                style={{ color: "var(--text-secondary)" }}
                data-testid="scoping-status"
              >
                {scopingStatus(scoping)}
              </div>
            </div>
            <p
              id="scoping-help"
              className="text-[11px] mt-1"
              style={{ color: "var(--text-secondary)" }}
            >
              How many scoping sessions this organization can start in a day, across every Team.
              Whole numbers from 1 to {scoping.maxSessionsPerDay}.
            </p>
            {!scopingValid && (
              <p className="text-[11px] mt-1" style={{ color: "var(--error)" }}>
                Enter a whole number from 1 to {scoping.maxSessionsPerDay}.
              </p>
            )}
          </>
        )}
      </section>

      {/* ── Per-developer budgets ────────────────────────────────── */}
      <section>
        <h2
          className="text-[14px] font-semibold mb-1 flex items-center gap-2"
          style={{ color: "var(--text-primary)" }}
        >
          <Users size={14} style={{ color: "var(--accent)" }} />
          Per-developer budgets
        </h2>
        <p className="text-[12px] mb-3" style={{ color: "var(--text-secondary)" }}>
          {data.developerBudgetsEnforced
            ? "Enforced: a developer past their cap is refused."
            : "Not enforced — yagni_code.per_developer_budgets is off for this workspace, so caps are stored but nothing blocks."}
        </p>
        <div className="flex items-end gap-3 flex-wrap mb-3">
          <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
            <span className="block mb-1">Developer</span>
            <select
              value={capMemberId}
              onChange={(e) => setCapMemberId(e.target.value)}
              aria-label="Developer to cap"
              className="px-2 py-1 rounded border text-[13px]"
              style={{ borderColor: "var(--border)", color: "var(--text-primary)" }}
            >
              <option value="">
                {data.members.length === 0 ? "No members" : "Select a member…"}
              </option>
              {data.members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.email ?? m.displayName ?? m.userId}
                </option>
              ))}
            </select>
          </label>
          <label className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
            <span className="block mb-1">Daily cap ($/day)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={capInput}
              onChange={(e) => setCapInput(e.target.value)}
              aria-label="Developer daily cap in dollars"
              className="px-2 py-1 rounded border text-[13px] w-36"
              style={{ borderColor: "var(--border)", color: "var(--text-primary)" }}
            />
          </label>
          <button
            type="button"
            disabled={busy === "set-cap" || !capValid}
            onClick={() =>
              void mutate("set-cap", `/developer-budgets/${capMemberId}`, {
                method: "PUT",
                body: JSON.stringify({ dailyLimitMillicents: capMillis }),
              }).then(() => {
                setCapMemberId("");
                setCapInput("");
              })
            }
            className="px-3 py-1.5 rounded text-[12px] border-none cursor-pointer disabled:opacity-50"
            style={{ background: "var(--accent-muted)", color: "var(--accent)" }}
          >
            {busy === "set-cap" ? <Loader2 size={11} className="animate-spin" /> : "Set cap"}
          </button>
        </div>
        {!capValid && capMemberId !== "" && (
          <p className="text-[11px] mb-2" style={{ color: "var(--error)" }}>
            Enter a non-negative dollar amount for the selected developer.
          </p>
        )}
        {data.developerBudgets.length === 0 ? (
          <p className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
            No per-developer caps set. Absence of a cap is the unlimited state.
          </p>
        ) : (
          <table className="w-full text-[12px]" style={{ color: "var(--text-primary)" }}>
            <thead>
              <tr style={{ color: "var(--text-secondary)" }}>
                <th className="text-left font-medium pb-1">Developer</th>
                <th className="text-right font-medium pb-1">Spent today</th>
                <th className="text-right font-medium pb-1">Daily cap</th>
                <th className="pb-1" />
              </tr>
            </thead>
            <tbody>
              {data.developerBudgets.map((b) => {
                const busyKey = `budget:${b.userId}`;
                return (
                  <tr key={b.userId}>
                    <td className="py-1">{b.email ?? b.displayName ?? b.userId}</td>
                    <td className="py-1 text-right">{formatDollars(b.spentTodayMillicents)}</td>
                    <td className="py-1 text-right">{formatDollars(b.dailyLimitMillicents)}</td>
                    <td className="py-1 text-right">
                      <button
                        type="button"
                        disabled={busy === busyKey}
                        onClick={() =>
                          void mutate(busyKey, `/developer-budgets/${b.userId}`, {
                            method: "PUT",
                            body: JSON.stringify({ dailyLimitMillicents: null }),
                          })
                        }
                        className="px-2 py-0.5 rounded text-[11px] border-none cursor-pointer disabled:opacity-50"
                        style={{
                          background: "rgba(var(--ink-rgb), 0.06)",
                          color: "var(--text-secondary)",
                        }}
                      >
                        {busy === busyKey ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          "Clear cap"
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <WorkspaceTeamsSection workspaceId={workspaceId} />
    </div>
  );
}

const REVIEW_LINE_LABELS = {
  flagEnabled: "reviews flag",
  githubConnected: "GitHub connected",
  proctorEngaged: "Proctor engaged",
  reposAttached: "repos attached",
  reposClaimed: "repos reviewed here",
  rhythmActive: "automatic reviews",
} as const;

/**
 * One job line on a Team (ADR-0044, Task 6). The operator reads, in order: who
 * holds it, what it says, who is accountable for the act, what starts it, when
 * it last fired and next fires, and the one status word. Everything here comes
 * from the same `readTeamJobStatus` computation the customer's Team page shows,
 * so this pane can never disagree with what they are looking at.
 */
function TeamJobLine({
  job,
  onSave,
}: {
  job: AdminWorkspaceTeamJobLine;
  onSave: (jobId: string, patch: AdminTeamLinePatch) => Promise<string | null>;
}) {
  const [editing, setEditing] = useState(false);
  const readinessWord =
    job.readiness === "needs_setup"
      ? `needs setup: ${job.readinessReason ? jobReadinessPhrase(job.readinessReason) : "something is missing"}`
      : job.readiness;
  const authorityColor = job.authority === "autonomous" ? "var(--success)" : "var(--accent)";
  return (
    <li
      className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px]"
      style={{ color: "var(--text-secondary)" }}
      data-testid={`workspace-team-job-${job.jobId}`}
    >
      <span style={{ color: "var(--text-primary)" }}>{job.agentName}</span>
      <span>{job.sentence}</span>
      {job.authority && <span style={{ color: authorityColor }}>{job.authority}</span>}
      <span>· {job.trigger}</span>
      {!job.filterResolves && (
        <span style={{ color: "var(--error)" }}>· filter does not resolve</span>
      )}
      <span>· last {formatRelative(job.lastFireAt)}</span>
      <span>· next {formatRelative(job.nextFireAt)}</span>
      <span
        style={{
          color: job.readiness === "needs_setup" ? "var(--error)" : "var(--text-secondary)",
        }}
      >
        · {readinessWord}
      </span>
      {job.stoppedReason && (
        <span style={{ color: "var(--error)" }}>· stopped: {job.stoppedReason}</span>
      )}
      {job.controls && !editing && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-[11px] px-1.5 rounded border-none cursor-pointer"
          style={{ background: "rgba(var(--ink-rgb), 0.06)", color: "var(--accent)" }}
          data-testid={`workspace-team-job-edit-${job.jobId}`}
        >
          Edit
        </button>
      )}
      {job.controls && editing && (
        <div className="basis-full">
          <TeamLineControls
            jobId={job.jobId}
            active={job.active}
            controls={job.controls}
            onSave={(patch) => onSave(job.jobId, patch)}
            onClose={() => setEditing(false)}
          />
        </div>
      )}
    </li>
  );
}

const REPO_STATUS_LABELS = {
  reviewing_here: "reviewed here",
  held_elsewhere: "reviewed elsewhere",
  unclaimed: "not reviewed yet",
  narrowed_out: "left out by Scope",
} as const;

/**
 * The workspace's Teams (2026-09-04): who is engaged on each at what
 * Authority, which repositories it attaches and who reviews each, and the
 * same readiness lines the customer's Proctor pane shows, so that "it isn't
 * reviewing" is answerable from here without asking the customer. Each line
 * can be edited in place (2026-10-05), because the operator is not a member of
 * the customer's workspace and the Team page is closed to them.
 */
function WorkspaceTeamsSection({ workspaceId }: Props) {
  const [data, setData] = useState<AdminWorkspaceTeamsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  // The one way back for an archived Team (it is hidden everywhere the
  // customer looks). The route answers with the refreshed overview, so the
  // row flips in place.
  const restore = useCallback(
    async (deskId: string) => {
      setRestoring(deskId);
      setError(null);
      try {
        const res = await fetch(
          `/api/admin/workspace-controls/${encodeURIComponent(workspaceId)}/teams/${encodeURIComponent(deskId)}/restore`,
          { method: "POST" },
        );
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? `Request failed (${res.status})`);
        }
        setData((await res.json()) as AdminWorkspaceTeamsResponse);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to restore team");
      } finally {
        setRestoring(null);
      }
    },
    [workspaceId],
  );

  // A line edit answers with the refreshed overview, so the row flips in
  // place. A refusal comes back as the server's sentence for the editor.
  const saveLine = useCallback(
    async (deskId: string, jobId: string, patch: AdminTeamLinePatch): Promise<string | null> => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), LINE_SAVE_TIMEOUT_MS);
      try {
        const res = await fetch(
          `/api/admin/workspace-controls/${encodeURIComponent(workspaceId)}/teams/${encodeURIComponent(deskId)}/lines/${encodeURIComponent(jobId)}`,
          {
            method: "PATCH",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(patch),
            signal: controller.signal,
          },
        );
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          return body?.error ?? `Request failed (${res.status})`;
        }
        setData((await res.json()) as AdminWorkspaceTeamsResponse);
        return null;
      } catch (err) {
        if (controller.signal.aborted) {
          return "The save did not answer in time. Reload the Teams to see whether it landed.";
        }
        return err instanceof Error ? err.message : "Failed to save the line";
      } finally {
        clearTimeout(timer);
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    void (async () => {
      try {
        const res = await fetch(
          `/api/admin/workspace-controls/${encodeURIComponent(workspaceId)}/teams`,
        );
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? `Request failed (${res.status})`);
        }
        const body = (await res.json()) as Partial<AdminWorkspaceTeamsResponse> | null;
        // A stubbed or older backend answering this path with another shape
        // must not take the whole controls page down with it.
        if (!body || !body.workspace || !Array.isArray(body.teams)) {
          throw new Error("Unexpected teams payload");
        }
        if (!cancelled) setData(body as AdminWorkspaceTeamsResponse);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load teams");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  return (
    <section data-testid="workspace-teams">
      <h2
        className="text-[14px] font-semibold mb-1 flex items-center gap-2"
        style={{ color: "var(--text-primary)" }}
      >
        <Users size={14} style={{ color: "var(--accent)" }} />
        Teams
      </h2>
      {error && (
        <div className="text-[12px]" style={{ color: "var(--error)" }} role="alert">
          {error}
        </div>
      )}
      {!data && !error && (
        <div
          className="flex items-center gap-2 text-[12px]"
          style={{ color: "var(--text-secondary)" }}
        >
          <Loader2 size={12} className="animate-spin" />
          Loading teams…
        </div>
      )}
      {data && (
        <>
          <p
            className="text-[12px] mb-2"
            style={{ color: "var(--text-secondary)" }}
            data-testid="workspace-teams-context"
          >
            reviews flag {data.workspace.codeReviewEnabled ? "on" : "off"} · residency{" "}
            {data.workspace.dataResidency ?? "unrestricted"} · pins{" "}
            {data.workspace.modelPins.length === 0
              ? "none"
              : data.workspace.modelPins
                  .map((pin) => `${pin.tier} → ${pin.provider}/${pin.model}`)
                  .join(", ")}
          </p>
          {/* An older backend sends no workspace switch. */}
          {data.workspace.buildLane && (
            <div className="mb-2">
              <WorkspaceLaneSelect
                workspaceId={workspaceId}
                lane={data.workspace.buildLane}
                refusal={data.workspace.buildLaneRefusal ?? null}
                onSaved={(buildLane) =>
                  setData(
                    (prev) => prev && { ...prev, workspace: { ...prev.workspace, buildLane } },
                  )
                }
              />
            </div>
          )}
          {data.teams.length === 0 ? (
            <p className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
              No Teams in this workspace.
            </p>
          ) : (
            <div className="space-y-3">
              {data.teams.map((team) => (
                <div
                  key={team.deskId}
                  className="rounded p-3"
                  style={{ background: "rgba(var(--ink-rgb), 0.04)" }}
                  data-testid={`workspace-team-${team.deskId}`}
                >
                  <div
                    className="text-[13px] font-medium flex items-center gap-2 flex-wrap"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {team.name}
                    {team.archived ? " · archived" : ""}
                    {team.archived && (
                      <button
                        type="button"
                        className="text-[11px] font-medium px-2 py-0.5 rounded border"
                        style={{ color: "var(--accent)", borderColor: "var(--accent)" }}
                        disabled={restoring === team.deskId}
                        onClick={() => void restore(team.deskId)}
                        data-testid={`workspace-team-restore-${team.deskId}`}
                      >
                        {restoring === team.deskId ? "Restoring…" : "Restore"}
                      </button>
                    )}
                  </div>
                  {/* An older backend sends no lane, and has no route to set one. */}
                  {team.lane && (
                    <TeamLaneSelect
                      workspaceId={workspaceId}
                      deskId={team.deskId}
                      lane={team.lane}
                      overriddenBy={
                        data.workspace.buildLane
                          ? workspaceOverride(data.workspace.buildLane, team.lane)
                          : null
                      }
                      onSaved={setData}
                    />
                  )}
                  {/* The lines themselves, one row each. This replaced the
                      per-engagement level line as the Team's real state: a
                      level said nothing about whether anything was firing. */}
                  {(team.jobs ?? []).length === 0 ? (
                    <div
                      className="text-[12px] mt-1"
                      style={{ color: "var(--text-secondary)" }}
                      data-testid={`workspace-team-jobs-${team.deskId}`}
                    >
                      no lines on this Team
                    </div>
                  ) : (
                    <ul
                      className="mt-1 space-y-0.5"
                      data-testid={`workspace-team-jobs-${team.deskId}`}
                    >
                      {(team.jobs ?? []).map((job) => (
                        <TeamJobLine
                          key={job.jobId}
                          job={job}
                          onSave={(jobId, patch) => saveLine(team.deskId, jobId, patch)}
                        />
                      ))}
                    </ul>
                  )}
                  {/* The legacy per-Agent aggregate, labelled as such: nothing
                      gates on it and it drops when ADR-0044 contracts. */}
                  <div className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
                    {team.engagements.length === 0
                      ? "nobody engaged"
                      : `set levels (legacy): ${team.engagements
                          .map(
                            (e) =>
                              `${e.displayName} · ${e.level}${e.enabled ? "" : " · stood down"}`,
                          )
                          .join(" / ")}`}
                  </div>
                  <div className="text-[12px] mt-1" style={{ color: "var(--text-secondary)" }}>
                    {team.repos.length === 0
                      ? "no repositories attached"
                      : team.repos
                          .map(
                            (repo) =>
                              `${repo.ref} · ${REPO_STATUS_LABELS[repo.status]}${
                                repo.status === "held_elsewhere" && repo.claimingDeskName
                                  ? ` (${repo.claimingDeskName})`
                                  : ""
                              }`,
                          )
                          .join(" / ")}
                  </div>
                  <ul
                    className="flex flex-wrap gap-x-3 text-[11px] mt-1"
                    data-testid={`workspace-team-review-${team.deskId}`}
                  >
                    {(Object.keys(REVIEW_LINE_LABELS) as (keyof typeof REVIEW_LINE_LABELS)[]).map(
                      (key) => (
                        <li
                          key={key}
                          style={{
                            color: team.review[key] ? "var(--text-secondary)" : "var(--error)",
                          }}
                        >
                          {team.review[key] ? "✓" : "○"} {REVIEW_LINE_LABELS[key]}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
